import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { Params } from "@/types";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import { isAxiosError } from "axios";

const endpoint = {
  employees: "/employees",
  workHour: `/employees-work-hour`,
  daysOfWeek: `/days-of-week`,
};

export const fetchEmployees = createAsyncThunk(
  "employees/fetchEmployees",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.employees, { params });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        // A API pode retornar 404 quando não existem colaboradores cadastrados.
        // Nessa situação, tratamos como lista vazia para evitar erro na UI/onboarding.
        if (error.response?.status === 404) {
          const emptyPayload = {
            status: "success",
            message: "Employees retrieved successfully",
            data: [],
          };
          return emptyPayload as any;
        }

        const errorMessage = error?.response?.data as {
          error?: string;
        };
        return thunkAPI.rejectWithValue({
          error: errorMessage?.error || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro ao carregar colaborador",
        });
      }
    }
  },
);

// Função auxiliar para extrair mensagens de erro de diferentes formatos
const extractErrorMessage = (errorData: any): string => {
  if (!errorData) return "Erro desconhecido";

  // Formato: { "error": "..." }
  if (errorData.error && typeof errorData.error === "string") {
    return errorData.error;
  }

  // Formato: { "message": "..." }
  if (errorData.message && typeof errorData.message === "string") {
    return errorData.message;
  }

  // Formato: { "errors": { "campo": ["mensagem1", "mensagem2"] } }
  if (errorData.errors && typeof errorData.errors === "object") {
    const errorMessages: string[] = [];
    Object.values(errorData.errors).forEach((fieldErrors: any) => {
      if (Array.isArray(fieldErrors)) {
        errorMessages.push(...fieldErrors);
      } else if (typeof fieldErrors === "string") {
        errorMessages.push(fieldErrors);
      }
    });
    if (errorMessages.length > 0) {
      return errorMessages.join(". ");
    }
  }

  // Fallback: tentar pegar qualquer string no objeto
  for (const key in errorData) {
    if (typeof errorData[key] === "string" && errorData[key]) {
      return errorData[key];
    }
  }

  return "Erro ao processar a solicitação";
};

export const createEmployee = createAsyncThunk(
  "employees/createEmployee",
  async (data: any, thunkAPI) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axiosInstance.post(endpoint.employees, data, {
        headers: isFormData
          ? { "Content-Type": "multipart/form-data" }
          : undefined,
      });

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error:
            errorMessage || error.message || "Erro ao cadastrar funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao cadastrar funcionário",
        });
      }
    }
  },
);

export const updateEmployee = createAsyncThunk(
  "employees/updateEmployee",
  async (data: any, thunkAPI) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.employees}/${data.id}`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error:
            errorMessage || error.message || "Erro ao atualizar funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar funcionário",
        });
      }
    }
  },
);

export const updatePasswordEmployee = createAsyncThunk(
  "employees/updatePasswordEmployee",
  async (
    {
      id,
      password,
      password_confirmation,
    }: { id: string; password: string; password_confirmation: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(`/employees/${id}/password`, {
        password,
        password_confirmation,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error:
            errorMessage ||
            error.message ||
            "Erro ao atualizar senha do funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar senha do funcionário",
        });
      }
    }
  },
);

export const deleteEmployee = createAsyncThunk(
  "employees/deleteEmployee",
  async (id: number, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.employees}/${id}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error: errorMessage || error.message || "Erro ao excluir funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir funcionário",
        });
      }
    }
  },
);

export const createEmployeeWorkHour = createAsyncThunk(
  "employees/createEmployeeWorkHour",
  async (data: any, thunkAPI) => {
    try {
      const response = await axiosInstance.post(endpoint.workHour, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error:
            errorMessage ||
            error.message ||
            "Erro ao criar horário de trabalho do funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error:
            "Erro desconhecido ao criar horário de trabalho do funcionário",
        });
      }
    }
  },
);

export const fetchEmployeeWorkHours = createAsyncThunk(
  "employees/fetchEmployeeWorkHours",
  async (employeeId: number | string | null, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `${endpoint.workHour}/${employeeId}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return thunkAPI.rejectWithValue({
          error: errorMessage?.message || error.message,
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro ao carregar horários de trabalho do funcionário",
        });
      }
    }
  },
);

export const deleteEmployeeWorkHour = createAsyncThunk(
  "employeeHours/deleteEmployeeWorkHour",
  async (data: { workHourId: number; employeeId: number }, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.workHour}/${data.workHourId}`,
      );
      return {
        ...response.data,
        employeeId: data.employeeId, // Incluir o employeeId na resposta para uso no slice
      };
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao excluir horário de trabalho do funcionário",
      );
    }
  },
);

// Update work hour (editar horário existente)
export const updateEmployeeWorkHour = createAsyncThunk(
  "employeeHours/updateEmployeeWorkHour",
  async (
    data: { workHourId: number; employeeId: number; payload: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.workHour}/${data.workHourId}`,
        data.payload,
      );
      return {
        ...response.data,
        employeeId: data.employeeId,
      };
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao atualizar horário de trabalho do funcionário",
      );
    }
  },
);

//Fetch DaysEmployee
export const fetchDaysEmployee = createAsyncThunk(
  "daysOfWeekEmployee/fetchdaysOfWeekEmployee",
  async (params: Params, thunkAPI) => {
    try {
      const response = await axiosInstance.get(endpoint.daysOfWeek);
      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar dias da semana do funcionário",
      );
    }
  },
);

//Upload Employee Image
export const uploadEmployeeImage = createAsyncThunk(
  "employees/uploadEmployeeImage",
  async (data: { employeeId: string | number; image: File }, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("image", data.image);
      // Novo endpoint inclui rota completa já montada via baseURL dinâmica: /establishment/{slug}/employees/{id}/upload-image
      const response = await axiosInstance.post(
        `${endpoint.employees}/${data.employeeId}/upload-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorData = error?.response?.data;
        const errorMessage = extractErrorMessage(errorData);
        return thunkAPI.rejectWithValue({
          error:
            errorMessage ||
            error.message ||
            "Erro ao fazer upload da imagem do funcionário",
        });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao fazer upload da imagem do funcionário",
        });
      }
    }
  },
);
